import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';

import { NotificationManager } from '@atlas/core';
import { FieldType } from '@atlas/payments';
import { usePaymentsForm } from '@atlas/payments/react';
import { CopyButton, CopyField, DropdownField, InputNumericField, InputTextField } from '@atlas/ui';

import { QRCodeField } from 'src/common/Notifications/Popups/PaymentsFormPopup/QRCodeField';
import { RedirectButton } from 'src/common/Notifications/Popups/PaymentsFormPopup/RedirectButton';
import { SubmitButton } from 'src/common/Notifications/Popups/PaymentsFormPopup/SubmitButton';
import { TextField } from 'src/common/Notifications/Popups/PaymentsFormPopup/TextField';
import { WarningTextField } from 'src/common/Notifications/Popups/PaymentsFormPopup/WarningTextField';

const PaymentsForm = ({ formConfig, interpolationValues, onSubmit, renderCustomField }) => {
  const { fields, options, setInterpolationData, setValue, validate, values } =
    usePaymentsForm(formConfig);
  useEffect(() => {
    setInterpolationData(interpolationValues);
  }, [interpolationValues, setInterpolationData]);

  const [errors, setErrors] = React.useState(null);

  const handleCopy = useCallback(() => {
    NotificationManager.notify({
      message: 'copied_data',
      status: 'success',
      type: 'notification',
    });
  }, []);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      const errors = validate();

      setErrors(errors);

      if (errors.size === 0) {
        onSubmit(values);
      }
    },
    [onSubmit, validate, values],
  );

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      {fields.map(
        ({
          dropdownOptions,
          fieldType,
          id,
          isReadOnly,
          label,
          mappingKey,
          placeholder,
          required,
          value,
        }) => {
          if (renderCustomField) {
            const custom = renderCustomField({ fieldType, id, label, mappingKey, value });
            if (custom !== false) return custom;
          }

          const fieldErrors = errors?.get(mappingKey)?.map(error => error.message);

          return (
            <div className="payments-form-field" key={mappingKey || id}>
              {[FieldType.STATIC_TEXT, FieldType.DYNAMIC_TEXT].includes(fieldType) && (
                <TextField text={value} />
              )}
              {fieldType === FieldType.INPUT_TEXT_FIELD && (
                <InputTextField
                  autoFocus={options.autofocusField === mappingKey}
                  errors={fieldErrors}
                  isReadOnly={isReadOnly}
                  isRequired={required}
                  label={label}
                  name={mappingKey}
                  onChange={e => setValue(mappingKey, e.target.value)}
                  value={value}
                />
              )}

              {fieldType === FieldType.INPUT_NUMERIC_FIELD && (
                <InputNumericField
                  autoFocus={options.autofocusField === mappingKey}
                  errors={fieldErrors}
                  isReadOnly={isReadOnly}
                  label={label}
                  name={mappingKey}
                  onChange={e => setValue(mappingKey, e.target.value)}
                  value={(value ?? '').toString()}
                />
              )}

              {fieldType === FieldType.DROP_DOWN && (
                <DropdownField
                  errors={fieldErrors}
                  isReadOnly={isReadOnly}
                  label={label}
                  name={mappingKey}
                  onChange={value => setValue(mappingKey, value)}
                  options={dropdownOptions}
                  placeholder={placeholder}
                  value={value}
                />
              )}

              {fieldType === FieldType.QR_CODE && <QRCodeField label={label} value={value} />}
              {fieldType === FieldType.REDIRECT_BUTTON && (
                <RedirectButton label={label} value={value} />
              )}
              {fieldType === FieldType.COPY_BUTTON && (
                <CopyButton label={label} onCopy={handleCopy} value={value} />
              )}
              {fieldType === FieldType.COPY_FIELD && (
                <CopyField label={label} onCopy={handleCopy} value={value} />
              )}
              {fieldType === FieldType.WARNING_TEXT && <WarningTextField value={value} />}
              {fieldType === FieldType.CONFIRMATION_BUTTON && <SubmitButton label={label} />}
            </div>
          );
        },
      )}
    </form>
  );
};

PaymentsForm.propTypes = {
  formConfig: PropTypes.object,
  interpolationValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderCustomField: PropTypes.func,
};

export default PaymentsForm;
